import { useGetFormBuilderAccessToken } from "@hooks/useGetFormBuilderAccessToken";
import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Container, Grid, Paper, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faLinkSlash } from "@fortawesome/free-solid-svg-icons";
import { generateUUID } from "@services/api/authentication/generate-uuid/generateUUID";
import { buildOfferApplicantProps } from "@services/offerApplicant/buildOfferApplicantProps";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ALL_STATUS_NOT_READ_ONLY, OfferApplicantStatus } from "./../../../types/OfferApplicantStatus.interface";
import VerifyEmailForm from "@components/ui/offer-applicant/VerifyEmailForm";
import { enqueueSnackbar } from "notistack";
import verifyOfferApplicantEmail from "@services/api/offer-applicant/verifyOfferApplicantEmail/verifyOfferApplicantEmail";
import { OfferApplicantValue } from "@services/offerApplicant/offerApplicant.type";
import { useLanguage } from "./../../../contexts/LanguageContext";
import { OFFER_PUBLIC_LIST_PATH } from "@routes/path";
import { useNavigate } from "react-router-dom";
import translations from "./translations.json";
import ApplicationSubmitted from "@components/ui/offer-applicant/ApplicationSubmitted";
import IdentificationForm from "@components/ui/offer-applicant/IdentificationForm";
import { Loading } from "@components/common/Loading";
import initiateOfferApplicant from "@services/api/offer-applicant/initiateOfferApplicant/initiateOfferApplicant";
import resendApplicantCode from "@services/api/offer-applicant/resendApplicantCode/resendApplicantCode";
import ApplicationInformation from "./ApplicationInformation";
import setAccessToken from "@services/offerApplicant/setAccessTokenLS";
import { getPublicOfferByUUID } from "@services/api/offer/getPublicOfferByUUID/getPublicOfferByUUID";
import updateOfferApplicant from "@services/api/offer-applicant/update/update";
type SupportedLanguage = keyof typeof translations;

const ApplyOfferApplicant = () => {

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const offerUUID = searchParams.get('o');     // "1449"
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;

    const [defaultValue, setDefaultValue] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState<'identification' | 'verification' | 'information' | 'submitted'>('identification');
    const [offer, setOffer] = useState<any | null>(null);
    const [emailAddress, setEmailAddress] = useState<string | null>(null);
    const [oldStatus, setOldStatus] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingNextStep, setLoadingNextStep] = useState<boolean>(false);
    const [isUpdate, setIsUpdate] = useState<boolean>(false);


    const handleRefreshStatus = async (newStatus: OfferApplicantStatus) => {
        setOldStatus(newStatus);
    };

    const buildDefaultProps = async () => {
        setLoading(true);
        if (offerUUID) {
            const uuid = await generateUUID();
            const offerResponse = await getPublicOfferByUUID({ uuid: offerUUID as any });
            if (!offerResponse?.data?.offer || !offerResponse?.data?.offerBuilder) {
                setError("Offer not found");
                return;
            }
            const props = buildOfferApplicantProps({
                uuid,
                offer: offerResponse?.data?.offerBuilder?.id,
                status: OfferApplicantStatus.EMAIL_NOT_VALIDATED,
                submittedLanguage: ['mg', 'en', 'fr'].includes(language?.toLowerCase() || '') ? language?.toLowerCase() : 'en',
                recruiterEmail: offerResponse?.data?.offer?.recruiter_email || "",
            });
            setDefaultValue(props);
            setOffer(offerResponse?.data || null);
        }
        setLoading(false);
    }

    useEffect(() => {
        buildDefaultProps();
    }, [offerUUID]);

    const handleCreateSubmit = async (data: any) => {
        await updateOfferApplicant(data);
        setEmailAddress(data?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.emailAddress] || null);
        setStep('submitted');
        await buildDefaultProps();
    };

    const handleIdentificationSubmit = async (email: string) => {
        setLoadingNextStep(true);
        const response = await initiateOfferApplicant(parseInt(offer?.offerBuilder?.id || "0", 10), email, language);
        if (response?.data?.initiated) {
             const offerApplicant = response?.data?.offerApplicantResponse;
             setEmailAddress(offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.emailAddress]);
             setIsUpdate(response?.data?.isUpdate);
             setDefaultValue((currentValue: any) => ({
                ...currentValue,
                uuid: offerApplicant?.uuid,
                [OfferApplicantValue.group]: {
                    ...currentValue?.[OfferApplicantValue.group],
                    ...offerApplicant?.[OfferApplicantValue.group],
                    [OfferApplicantValue.subGroup.information]: {
                        ...currentValue?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information],
                        ...offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information],
                        [OfferApplicantValue.field.emailAddress]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.emailAddress],
                        [OfferApplicantValue.field.fullName]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.fullName] == '-' ? '' : offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.fullName],
                        [OfferApplicantValue.field.phoneNumber]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.phoneNumber] == '-' ? '' : offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.phoneNumber],
                        [OfferApplicantValue.field.recruiterEmail]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.recruiterEmail] || "",
                    },
                },
             }));
             if (!response?.data?.authenticated) {
                enqueueSnackbar(content.initiate.success, { variant: "success" });
             }
             setOldStatus(offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.status] || "");
             setStep(response?.data?.authenticated ? 'information' : 'verification');
             return;

        }
        enqueueSnackbar(content.initiate.error, { variant: "error" });
        setLoadingNextStep(false);
    };

    const handleVerifySubmit = async (code: string) => {
        const response = await verifyOfferApplicantEmail(parseInt(offer?.offerBuilder?.id || "0", 10), emailAddress || "", code);
        if (response?.data?.verified) {
            enqueueSnackbar(content.successVerify, { variant: "success" });
            const offerApplicant = response?.data?.offerApplicant;
            const oldStatus = offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.status];
            setDefaultValue((currentValue: any) => ({
                ...currentValue,
                [OfferApplicantValue.group]: {
                    ...currentValue?.[OfferApplicantValue.group],
                    ...offerApplicant?.props?.[OfferApplicantValue.group],
                    [OfferApplicantValue.subGroup.information]: {
                        ...currentValue?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information],
                        ...offerApplicant?.props?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information],
                        [OfferApplicantValue.field.status]: oldStatus == OfferApplicantStatus.EMAIL_VALIDATED_DRAFT ? OfferApplicantStatus.SUBMITTED_TO_IA : oldStatus,
                        [OfferApplicantValue.field.fullName]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.fullName] == '-' ? '' : offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.fullName],
                        [OfferApplicantValue.field.phoneNumber]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.phoneNumber] == '-' ? '' : offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.phoneNumber],
                        [OfferApplicantValue.field.recruiterEmail]: offerApplicant?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.recruiterEmail] || "",
                    },
                },
            }));
            setAccessToken(response?.data?.token, response?.data?.expiresAt);
            setOldStatus(oldStatus);
            setStep('information');
            return;
        }
        enqueueSnackbar(content.failedVerify, { variant: "error" });
    };

    const handleResendCode = async () => {
        const response = await resendApplicantCode(parseInt(offer?.offerBuilder?.id || "0", 10), emailAddress || "", language);
        if (response?.data?.resent) {
            enqueueSnackbar(content.initiate.success, { variant: "success" });
            return;
        }
        throw new Error(content.initiate.error);
    };

    const { loading: loadingFormBuilderAccessToken, formBuilderAccessToken } = useGetFormBuilderAccessToken(["recruiter", "offer", "offer-category", "offer-application"]);

    const isReadOnly = useMemo(() => {
        return !ALL_STATUS_NOT_READ_ONLY.includes(defaultValue?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.status]);
    }, [defaultValue]);

    if (!offerUUID) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#0d1117" }}>
                <Box sx={{ textAlign: "center", p: 4 }}>
                    <FontAwesomeIcon icon={faLinkSlash} style={{ fontSize: "48px", color: "#64748b", marginBottom: "16px" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#f8fafc", mb: 1 }}>
                        {content.noOffer}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        {content.invalidLink}
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#0d1117" }}>
                <Box sx={{ textAlign: "center", p: 4 }}>
                    <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: "48px", color: "#ef4444", marginBottom: "16px" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#f8fafc", mb: 1 }}>
                        {content.errorTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        {error}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                backgroundColor: '#0d1117', 
                minHeight: '100vh', 
                pt: 4, 
                mt: 4,
                pb: 6 
            }}
        >
            <Container maxWidth="xl">
                {/* Bouton retour placé proprement en haut */}
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: 'space-between', mb: 3 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(`${OFFER_PUBLIC_LIST_PATH}?language=${encodeURIComponent(language)}&targetCountry=MG`)}
                        sx={{
                            width: 'auto',
                            color: '#ffffff',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid #23262d',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderColor: '#3b82f6',
                            },
                        }}
                    >
                        {content.back}
                    </Button>
                </Box>

                {/* Grid de Cards */}
                <Grid 
                    container 
                    spacing={3} 
                    justifyContent="center"
                >
                    {loadingFormBuilderAccessToken && (
                        <Grid item xs={12} display="flex" justifyContent="center">
                            <Loading />
                        </Grid>
                    )}
                    {!loadingFormBuilderAccessToken && (
                        <Grid item xs={12} md={10} lg={8} key={formBuilderAccessToken}>
                            {loading && <Loading />}
                            {!loading && (
                                <>
                                    {step === 'identification' && (
                                        <IdentificationForm
                                            offerText={`#${offer?.offer?.title || 'N/A'} - ${offer?.offer?.company_name || 'N/A'}`}
                                            defaultValue={defaultValue}
                                            onSubmit={handleIdentificationSubmit}
                                            loading={loadingNextStep}
                                        />
                                    )}
                                    {step === 'verification' && (
                                        <Paper elevation={0} sx={{ p: 4, width: 420, mx: 'auto', backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
                                            <VerifyEmailForm
                                                title={content.verifyTitle}
                                                subtitle={content.verifySubtitle}
                                                offerText={`#${offer?.offer?.title || 'N/A'} - ${offer?.offer?.company_name || 'N/A'}`}
                                                email={emailAddress || ""}
                                                onSubmit={handleVerifySubmit}
                                                onResend={handleResendCode}
                                                onBack={() => {
                                                    setLoadingNextStep(false);
                                                    setStep('identification');
                                                }}
                                            />
                                        </Paper>
                                    )}
                                    {step === 'information' && (
                                        <ApplicationInformation
                                            status={oldStatus}
                                            defaultValue={defaultValue}
                                            formBuilderAccessToken={formBuilderAccessToken}
                                            successMessage={isUpdate ? content.information.update.success : content.information.create.success}
                                            onSubmit={handleCreateSubmit}
                                            isReadOnly={isReadOnly}
                                            offerId={parseInt(offer?.offerBuilder?.id || "0", 10)}
                                            onRefreshStatus={handleRefreshStatus}
                                        />
                                    )}
                                    {step === 'submitted' && <ApplicationSubmitted isUpdate={isUpdate} />}
                                </>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Container>
        </Box>
    );
}

export default ApplyOfferApplicant;
